import { type BigDecimalRangeDto, type Currency, CURRENCY_SYMBOL_MAP, Language } from '@ev-tech/search-modules-api';

export const validateNumber = (value: string): boolean => {
  const numberRegex = /^[1-9]\d*$/;
  return numberRegex.test(value.toString());
};
export const validateRangeValues = (filter: BigDecimalRangeDto): BigDecimalRangeDto => {
  if (!filter.min || !filter.max) {
    return filter;
  }

  return {
    min: Math.min(filter.min, filter.max),
    max: Math.max(filter.min, filter.max),
  };
};

export type FormatNumberParams = {
  number: number;
  language: Language;
  shouldShowRoundedValue?: boolean;
};

export const formatNumber = (
  { number, language, shouldShowRoundedValue }: FormatNumberParams,
  opts: Intl.NumberFormatOptions = {},
): string => {
  // The formatted numbers in french language are using Narrow no break space https://www.codetable.net/decimal/8239 as thousands separator
  // and the E&V font is rendering this kind of white spaces bigger than usual white spaces like &nbsp;
  // here we are replacing all white spaces with a blank space character as a workaround
  // https://engelvoelkers.atlassian.net/browse/NS-3288?focusedCommentId=342373
  const formatted = new Intl.NumberFormat(language, { ...opts }).format(number).replaceAll(/\s+/g, ' ');

  // Replace currency code with symbol if it exists in CURRENCY_SYMBOL_MAP
  if (opts.style === 'currency' && opts.currency && CURRENCY_SYMBOL_MAP[opts.currency as Currency]) {
    return formatted.replace(opts.currency, CURRENCY_SYMBOL_MAP[opts.currency as Currency]);
  }

  return (shouldShowRoundedValue ? '~' : '') + formatted;
};

export const parseFormattedNumber = (value: string): string => {
  // remove space, comma or period from a string
  return value.replaceAll(/[\s,.]/g, '');
};

export const formatBigNumber = (number: number, language: Language = Language.en, maxDigits = 3): string => {
  if (Number.isNaN(number)) {
    return '';
  }
  let unit = '';
  let transformedNumber = number;

  if (number >= 1000) {
    unit = 'K';
    transformedNumber = number / 1000;
  }
  if (number >= 1_000_000) {
    unit = 'M';
    transformedNumber = number / 1_000_000;
  }

  const formattedNumber = formatNumber(
    { number: transformedNumber, language },
    number >= 1000
      ? {
          maximumSignificantDigits: maxDigits,
        }
      : {},
  );
  return `${formattedNumber}${unit}`;
};

export const formatPrice = (number: number, language: Language, opts: Intl.NumberFormatOptions = {}): string => {
  if (number <= 0) {
    return '';
  }
  return formatNumber(
    { number, language },
    {
      style: 'currency',
      maximumFractionDigits: opts.maximumFractionDigits || 0,
      minimumFractionDigits: opts.minimumFractionDigits || 0,
      currency: 'EUR',
      // [NS-7450] override grouping behavior, because for spanish language the grouping only starts after 5 digits by default
      useGrouping: true,
      ...opts,
    },
  );
};

export const formatPriceWithoutDecimals = (price: number, language: Language, currency: Currency) => {
  return formatPrice(price, language, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    currency,
  });
};

export const formatPriceWithDecimals = (price: number, language: Language, currency: Currency) => {
  // we add this check to avoid trailing zero e.g 12.00€ when formatting prices without decimals
  const isInteger = price % 1 === 0;
  return formatPrice(price, language, {
    maximumFractionDigits: isInteger ? 0 : 2,
    minimumFractionDigits: isInteger ? 0 : 2,
    currency,
  });
};
